import { Avatar, Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from "@mui/material"
import { CloseOutlined, EmailOutlined, PersonSearchOutlined, PhoneOutlined, SearchOutlined } from "@mui/icons-material"
import { FC, useState } from "react"
import { Apis, getImageUrl } from "../../helpers"

export interface FoundUser {
    _id: string
    firstName: string
    lastName: string
    userName?: string
    phone?: string
    email?: string
    profilePhotos?: any
}

interface Props {
    propertyId?: string
    isCollaborator?: boolean
    onSelect: (user: FoundUser) => void
    close: () => void
}
interface StateProps {
    value: string
    searching: boolean
    error: string | null
    user: FoundUser | null
}

const FindUserModal: FC<Props> = ({ propertyId, isCollaborator, onSelect, close }) => {
    const [state, setState] = useState<StateProps>({ value: '', searching: false, error: null, user: null })
    const { value, searching, error, user } = state

    const search = () => {
        if (!value.trim()) return
        setState(s => ({ ...s, searching: true, error: null, user: null }))
        Apis({
            module: 'Users',
            action: 'searchUser',
            value: value.trim(),
            propertyId,
            ...(isCollaborator ? { isCollaborator: true } : {})
        }).then((resp: any) => {
            setState(s => ({
                ...s,
                searching: false,
                user: resp?.user || null,
                error: resp?.user ? null : (resp?.error || 'No user found on Sheltergram.')
            }))
        }).catch(() => setState(s => ({ ...s, searching: false, error: 'Something went wrong. Please try again.' })))
    }

    return (
        <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>Find User</Typography>
                    <IconButton size="small" onClick={close}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {!user ? (
                    <>
                        <TextField
                            fullWidth
                            autoFocus
                            size="small"
                            label="Phone, email or username"
                            value={value}
                            onChange={e => setState(s => ({ ...s, value: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') search() }}
                            error={Boolean(error)}
                            helperText={error}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!value.trim() || searching}
                            onClick={search}
                            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchOutlined />}
                            sx={{ textTransform: 'none' }}
                        >
                            Search
                        </Button>
                    </>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', mb: 2 }}>
                            <Avatar src={getImageUrl(user.profilePhotos, 'small')} sx={{ width: 48, height: 48 }}>
                                <PersonSearchOutlined />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
                                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || '—'}
                                </Typography>
                                {user.phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                        <PhoneOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{user.phone}</Typography>
                                    </Box>
                                )}
                                {user.email && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                        <EmailOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{user.email}</Typography>
                                    </Box>
                                )}
                                {!user.phone && !user.email && user.userName && (
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.3 }} noWrap>{user.userName}</Typography>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                                onClick={() => setState(s => ({ ...s, user: null, value: '' }))}
                            >
                                Search Again
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ textTransform: 'none' }}
                                onClick={() => { onSelect(user); close() }}
                            >
                                Use This User
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    )
}

export default FindUserModal
